import { Badge, Box, Group, Select, Text } from '@mantine/core';
import { getSpecialtyCodes } from 'constants/specialtyCodes';
import { Provider } from 'interfaces';
import { forwardRef, useMemo } from 'react';

export interface ProviderSelectProps {
  providers: Provider[];
  value: string | null;
  onChange: (value: string | null, provider?: Provider | null) => void;
  placeholder?: string;
  label?: string;
  clearable?: boolean;
  searchable?: boolean;
  withinPortal?: boolean;
  disabled?: boolean;
  allowDeselect?: boolean; // for compatibility with some usages
  radius?: string;
  w?: string | number;
  style?: React.CSSProperties;
  dataTestId?: string;
}

interface ProviderOptionDetails {
  name: string;
  specialtyDescription?: string;
  specialtyCode?: string;
}

const getProviderOptionDetails = (
  provider: Provider
): ProviderOptionDetails => {
  const name = [provider.firstName, provider.lastName]
    .filter(Boolean)
    .join(' ');
  const specialty =
    provider.specialty ??
    getSpecialtyCodes(provider.province).find(
      ({ code }) => code === provider.specialtyCode
    );

  return {
    name,
    specialtyDescription: specialty?.description,
    specialtyCode: specialty?.code || provider.specialtyCode || undefined,
  };
};

// A centralized provider select to keep labeling / mapping consistent.
export const ProviderSelect = forwardRef<HTMLInputElement, ProviderSelectProps>(
  (
    {
      providers,
      value,
      onChange,
      placeholder = 'Select Provider',
      label = 'Provider',
      clearable = true,
      searchable = true,
      withinPortal = true,
      disabled = false,
      radius = 'lg',
      w = '100%',
      style,
      dataTestId,
      allowDeselect,
    },
    ref
  ) => {
    const { data, detailsByProviderID } = useMemo(() => {
      const detailsByProviderID = new Map<string, ProviderOptionDetails>();
      const data = (providers || []).map((provider) => {
        const details = getProviderOptionDetails(provider);
        detailsByProviderID.set(provider.id, details);

        return {
          label: details.specialtyDescription
            ? `${details.name} — ${details.specialtyDescription}`
            : details.name,
          value: provider.id,
        };
      });

      return { data, detailsByProviderID };
    }, [providers]);

    return (
      <Select
        ref={ref}
        placeholder={placeholder}
        data={data}
        renderOption={({ option }) => {
          const details = detailsByProviderID.get(option.value);

          if (!details) return option.label;

          return (
            <Box style={{ minWidth: 0 }}>
              <Text size="sm" fw={600} truncate>
                {details.name}
              </Text>
              <Group gap={6} wrap="nowrap">
                {details.specialtyDescription && (
                  <Text size="xs" c="dimmed" truncate>
                    {details.specialtyDescription}
                  </Text>
                )}
                {details.specialtyCode && (
                  <Badge
                    size="xs"
                    variant="light"
                    color="gray"
                    radius="sm"
                    styles={{
                      root: {
                        flexShrink: 0,
                        fontWeight: 500,
                      },
                    }}
                  >
                    {details.specialtyCode}
                  </Badge>
                )}
              </Group>
            </Box>
          );
        }}
        label={label}
        value={value || null}
        onChange={(val) => {
          const providerObj = providers.find((p) => p.id === val) || null;
          onChange(val, providerObj);
        }}
        radius={radius as any}
        w={w as any}
        clearable={clearable}
        searchable={searchable}
        comboboxProps={{ withinPortal }}
        style={style}
        disabled={disabled}
        data-testid={dataTestId || 'provider-select'}
        allowDeselect={allowDeselect}
      />
    );
  }
);
